/**
 * /web/api/auth React Query hooks.
 *
 * `useMe` is the single source of truth for "is the user logged in".
 * The query is set to retry: false so a 401 surfaces immediately
 * (the api client already redirects to /login).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface User {
  id: number;
  login: string;
  username: string | null;
  rights: string[];
  balance: number;
  deposit_address: Record<string, unknown> | null;
  web_ip_whitelist_enabled: boolean;
  /** True if the user has been issued an api_secret. The plaintext
   *  is never returned here — the playground uses this only to
   *  decide whether to show the endpoint cards or the
   *  "contact support" panel. */
  api_secret_set: boolean;
}

interface LoginRequest {
  login: string;
  password: string;
  totp_code?: string;
}

/**
 * Returned by the login endpoint when 2FA is required for the
 * given account but the body had no/invalid `totp_code`. The SPA
 * inspects ApiError.detail and re-renders the form with a code
 * field instead of an error.
 */
export function isTotpRequired(detail: unknown): boolean {
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { requires_totp?: unknown }).requires_totp === true
  );
}

interface LoginResponse {
  user: User;
}

export function useMe() {
  return useQuery<User, ApiError>({
    queryKey: ["me"],
    queryFn: () => api.get<User>("/web/api/me"),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hard-gate variant of `useMe`. Forces a fresh `/web/api/me` round-
 * trip on every mount and never serves stale cache. Use this from
 * authorization gates (`RequireRight` and friends) so a user who
 * tampers with the React Query cache from devtools (`setQueryData
 * (['me'], {rights: ['owner']})`) cannot survive a route mount: the
 * gate refetches and the server-truth rights overwrite the lie
 * before children render. The server still owns the actual auth
 * decision — this only stops the SPA shell from misleading the user.
 */
export function useFreshMe() {
  return useQuery<User, ApiError>({
    queryKey: ["me"],
    queryFn: () => api.get<User>("/web/api/me"),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<LoginResponse, ApiError, LoginRequest>({
    mutationFn: (body) =>
      api.post<LoginResponse>("/web/api/login", {
        body,
        noRedirectOn401: true,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => api.post("/web/api/logout"),
    onSettled: () => {
      qc.removeQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogoutAll() {
  const qc = useQueryClient();
  return useMutation<{ revoked: number }, ApiError, void>({
    mutationFn: () => api.post("/web/api/logout_all"),
    onSettled: () => {
      qc.removeQueries({ queryKey: ["me"] });
    },
  });
}
